import { useEffect } from 'react';
import isInputElement from '~/utils/isInputElement';

interface IUseCheckInputValidity {
    searchElementSelector: string;
    searchTriggerSelector?: string;
    isInstantSearch: boolean;
}

/**
 * Used for checking whether or not the input field is empty. This presupposes that 'required' is present on the input
 * element, so we can use the native browser API to report the element's validity, instead of implementing it.
 *
 * @param searchElementSelector - selector of input elements (config.nativeSearchId)
 * @param searchTriggerSelector - is used for selecting an element on the page, which intiates the search. For example a button or a magnifying glass icon.
 */
const useCheckInputValidity = ({
    searchElementSelector,
    searchTriggerSelector,
    isInstantSearch,
}: IUseCheckInputValidity) => {
    useEffect(() => {
        if (isInstantSearch && searchTriggerSelector) {
            const nativeSearchElements: Element[] = Array.from(document.querySelectorAll(`${searchElementSelector}`));

            const instantSearchTriggerElements: Element[] = Array.from(
                document.querySelectorAll(`${searchTriggerSelector}`)
            );

            const checkInputValidity = () => {
                nativeSearchElements.forEach((nativeSearchElement) => {
                    if (isInputElement(nativeSearchElement)) {
                        nativeSearchElement.reportValidity();
                    }
                });
            };

            const handleClick = (e: MouseEvent) => {
                e.preventDefault();
                checkInputValidity();
            };

            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                    checkInputValidity();
                }
            };

            const clickHandler = (event: Event) => handleClick(event as MouseEvent);
            const keyDownHandler = (event: Event) => handleKeyDown(event as KeyboardEvent);

            nativeSearchElements.forEach((nativeSearchElement) => {
                nativeSearchElement.addEventListener('keydown', keyDownHandler);
            });

            instantSearchTriggerElements.forEach((instantSearchTriggerElement) => {
                instantSearchTriggerElement.addEventListener('mousedown', clickHandler, true);
                instantSearchTriggerElement.addEventListener('click', clickHandler, true);
            });

            return () => {
                nativeSearchElements.forEach((nativeSearchElement) => {
                    nativeSearchElement.removeEventListener('keydown', keyDownHandler);
                });

                instantSearchTriggerElements.forEach((instantSearchTriggerElement) => {
                    instantSearchTriggerElement.removeEventListener('mousedown', clickHandler, true);
                    instantSearchTriggerElement.removeEventListener('click', clickHandler, true);
                });
            };
        }
    }, []);
};
export default useCheckInputValidity;
